/**
 * Widget-scoped recording hook using the core recorder
 *
 * This hook wraps the @refinevoice/core createRecorder to integrate with
 * React state and the widget's store context.
 */

import { createRecorder } from '@refinevoice/core';
import type { ConnectionState, RecorderController, TranscriptionChunk } from '@refinevoice/shared';
import type { Editor } from '@tiptap/react';
import { useCallback, useRef, useState } from 'react';
import {
  useWidgetAppStateStore,
  useWidgetConnectionStore,
  useWidgetRerecordStore,
  useWidgetTranscriptStore,
} from '../contexts/WidgetStoresContext';
import { useLanguageStore } from '../store/useLanguageStore';
import { useAudioLevelHistory } from './useAudioLevelHistory';

// Get backend URL - use relative URLs for same-origin, or refinevoice.com for production
const BACKEND_URL = (import.meta as any).env?.VITE_API_URL || '';

// Get widget key from environment or default to demo key for development
const WIDGET_KEY = (import.meta as any).env?.VITE_WIDGET_KEY || 'wk_free_demo';

export function useWidgetRecording(editor: Editor) {
  // Final transcript state for parent component to handle editor insertion
  // Include a unique id to force React effect to run on each new transcript
  const [finalTranscript, setFinalTranscript] = useState<{
    id: number;
    text: string;
    replaceSelection?: { from: number; to: number };
    words?: Array<{
      word: string;
      start: number;
      end: number;
      confidence: number;
      punctuated_word?: string;
    }>;
  } | null>(null);
  const finalTranscriptIdRef = useRef(0);

  // Recorder instance ref
  const recorderRef = useRef<RecorderController | null>(null);
  const selectionToReplaceRef = useRef<{ from: number; to: number } | null>(null);

  // Use widget-specific stores from context
  const { recordingState, setRecordingState, transcript, setInterimTranscript } =
    useWidgetTranscriptStore();

  const { connectionState, setConnectionState, setIsUsingLocalTranscription } =
    useWidgetConnectionStore();

  const { setIsRerecording, setRerecordWordIndex } = useWidgetRerecordStore();

  const { userSession, showError } = useWidgetAppStateStore();

  const { getLanguageCode, getWebSpeechCode } = useLanguageStore();
  const { clearHistory } = useAudioLevelHistory();

  const stopRecording = useCallback(() => {
    // Stop the recorder
    if (recorderRef.current) {
      recorderRef.current.stop();
      recorderRef.current = null;
    }

    // Clear audio level history
    clearHistory();

    // Reset local transcription flag
    setIsUsingLocalTranscription(false);

    // Reset rerecord mode
    setIsRerecording(false);
    setRerecordWordIndex(null);

    // Reset selection replacement
    selectionToReplaceRef.current = null;

    // Clear interim transcript
    setInterimTranscript('');

    setConnectionState('disconnected');

    // Simulate processing completion after a short delay
    setTimeout(() => {
      setRecordingState('idle');
    }, 500);
  }, [
    setRecordingState,
    setConnectionState,
    setIsUsingLocalTranscription,
    setIsRerecording,
    setRerecordWordIndex,
    setInterimTranscript,
    clearHistory,
  ]);

  /**
   * Handle final transcript - insert into editor or update state
   */
  const handleFinalTranscript = useCallback(
    (text: string, words?: TranscriptionChunk['words']) => {
      // Clear interim transcript
      setInterimTranscript('');

      // Increment id to force React effect to run on each new transcript
      const id = ++finalTranscriptIdRef.current;

      // Priority 1: Check if we're replacing a selection
      if (selectionToReplaceRef.current) {
        const { from, to } = selectionToReplaceRef.current;

        // Set final transcript for parent component to handle
        setFinalTranscript({
          id,
          text,
          replaceSelection: { from, to },
          words: words?.map((w) => ({
            word: w.word,
            start: w.startIndex,
            end: w.endIndex,
            confidence: w.confidence,
          })),
        });

        // Clear selection reference
        selectionToReplaceRef.current = null;
      }
      // Priority 2: Normal insertion at cursor
      else {
        setFinalTranscript({
          id,
          text,
          words: words?.map((w) => ({
            word: w.word,
            start: w.startIndex,
            end: w.endIndex,
            confidence: w.confidence,
          })),
        });
      }
    },
    [setInterimTranscript]
  );

  const startRecording = useCallback(async () => {
    try {
      // Capture current selection if any text is selected
      const { from, to } = editor.state.selection;
      if (from !== to) {
        selectionToReplaceRef.current = { from, to };
      }

      setRecordingState('connecting');
      setConnectionState('connecting');

      // Check if Web Speech API is available for local transcription when offline
      const SpeechRecognition =
        (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

      if (SpeechRecognition && !navigator.onLine) {
        // Use local Web Speech API when offline
        setIsUsingLocalTranscription(true);
        const recognition = new SpeechRecognition();

        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = getWebSpeechCode();

        recognition.onresult = (event: any) => {
          let finalText = '';

          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalText += `${transcript} `;
            }
          }

          if (finalText.trim()) {
            handleFinalTranscript(finalText.trim());
          }
        };

        recognition.onerror = (event: any) => {
          console.error('Speech recognition error:', event.error);
          setConnectionState('error');
          showError(
            'Speech Recognition Failed',
            'Could not start transcription. Please check microphone permissions and try again.'
          );
        };

        recognition.onend = () => {
          if (recordingState === 'recording') {
            try {
              recognition.start();
            } catch (error) {
              console.error('Failed to restart recognition:', error);
            }
          }
        };

        recognition.start();
        setConnectionState('connected');
        setRecordingState('recording');
        return;
      }

      // Calculate max recording time based on user session
      const maxRecordingTime = userSession?.type === 'anonymous' ? 60 : 300;

      // Create recorder with callbacks
      const recorder = createRecorder({
        backendUrl: BACKEND_URL,
        widgetKey: WIDGET_KEY,
        language: getLanguageCode(),
        autoStopAfterSilence: 30, // 30 seconds - longer timeout since interim_results not available via AI binding
        maxRecordingTime,
        onConnectionStateChange: (state: ConnectionState) => {
          setConnectionState(state);
          if (state === 'connected') {
            setRecordingState('recording');
          }
        },
        onTranscript: (chunk: TranscriptionChunk) => {
          if (chunk.text?.trim()) {
            if (chunk.type === 'final') {
              handleFinalTranscript(chunk.text, chunk.words);
            } else {
              setInterimTranscript(chunk.text);
            }
          }
        },
        onError: (error: Error) => {
          console.error('Recording error:', error);
          setConnectionState('error');

          // Check for time limit error
          if (error.message.includes('time limit')) {
            if (userSession?.type === 'anonymous') {
              showError(
                '1-Minute Limit Reached',
                'Sign in with your email for 5-minute recordings and 100 refinements/day!',
                'info'
              );
            } else {
              showError(
                '5-Minute Limit Reached',
                "You've reached the maximum recording time. Stop and refine your recording, or start a new one.",
                'info'
              );
            }
          } else {
            showError('Recording Failed', error.message);
          }

          stopRecording();
        },
      });

      recorderRef.current = recorder;

      // Start recording
      await recorder.start();
    } catch (error) {
      console.error('Failed to start recording:', error);
      setRecordingState('idle');
      setConnectionState('error');

      let errorTitle = 'Recording Failed';
      let errorMessage = 'Failed to start recording.';

      if (error instanceof Error) {
        errorMessage = error.message;

        if (error.message.includes('Microphone permission')) {
          errorTitle = 'Microphone Permission Denied';
        } else if (error.message.includes('No microphone')) {
          errorTitle = 'No Microphone Found';
        } else if (error.message.includes('Browser not supported')) {
          errorTitle = 'Browser Not Supported';
        } else if (error.message.includes('authenticate')) {
          errorTitle = 'Authentication Failed';
        } else if (error.message.includes('quota')) {
          errorTitle = 'Quota Exceeded';
        }
      }

      showError(errorTitle, errorMessage);
    }
  }, [
    editor,
    setRecordingState,
    setConnectionState,
    showError,
    stopRecording,
    getLanguageCode,
    getWebSpeechCode,
    setIsUsingLocalTranscription,
    setInterimTranscript,
    userSession,
    recordingState,
    handleFinalTranscript,
  ]);

  const startRerecordingSelection = useCallback(
    async (from: number, to: number) => {
      // Store selection range for replacement
      selectionToReplaceRef.current = { from, to };

      // Start recording (reuse existing logic)
      await startRecording();
    },
    [startRecording]
  );

  return {
    recordingState,
    transcript,
    finalTranscript,
    connectionState,
    startRecording,
    stopRecording,
    startRerecordingSelection,
    isRecording: recordingState === 'recording',
    isConnecting: recordingState === 'connecting',
    isProcessing: recordingState === 'processing',
  };
}
